import { fork } from '@cortex/utils/fork';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { coherenceLoginPath, keycloakLoginPath, resolveAuthMode } from './auth-mode.js';
import type { IAuthSession, IAuthStrategy, IConnectTokens } from './auth-strategy.js';
import { browserCoherenceEnv, createCoherenceAuth } from './coherence-auth.js';
import { createFormantAuthClient } from './formant-auth-client.js';
import { createKeycloakAuth } from './keycloak-auth.js';

export interface IAuthState {
  /** True after the bootstrap finishes (callback handled, existing session loaded). */
  ready: boolean;
  isAuthenticated: boolean;
  /** Access token (JWT). Send as Authorization: Bearer to the cortex API. */
  token: string | null;
  /** Display fields from the session, if available. */
  email: string | null;
  name: string | null;
  /** Org display name from the session (Robotics: fetched; Keycloak: null,
   *  derived from the token by useOrgContext). */
  orgName: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Current session's access + refresh tokens for a user-token MCP connect. */
  getConnectTokens: () => Promise<IConnectTokens | null>;
}

const AuthContext = createContext<IAuthState | null>(null);

/**
 * Pick the auth strategy for this load: cortex-Keycloak when the sticky
 * `?login=keycloak` break-glass is active, Coherence otherwise. Applies the
 * mode-resolution URL cleanup (strip the `login` param) as a side effect.
 */
function buildStrategy(): IAuthStrategy {
  const { mode, cleanedSearch } = resolveAuthMode(window.location.search, window.location.pathname);
  // `/coherence` and `/keycloak` are bootstrap markers for a login flow, not
  // routes. Once the mode is resolved (+ persisted in sessionStorage), send the
  // user to `/` so the router never sees the marker and the login returnUrl is `/`.
  const path =
    window.location.pathname === coherenceLoginPath ||
    window.location.pathname === keycloakLoginPath
      ? '/'
      : window.location.pathname;
  if (cleanedSearch !== window.location.search || path !== window.location.pathname) {
    window.history.replaceState({}, '', `${path}${cleanedSearch}${window.location.hash}`);
  }

  if (mode === 'keycloak') return createKeycloakAuth();

  const apiBase = import.meta.env.VITE_FORMANT_API_URL ?? 'https://api-dev.formant.io';
  const coherenceLoginUrl =
    import.meta.env.VITE_COHERENCE_LOGIN_URL ?? 'http://localhost:3005/login';
  return createCoherenceAuth({
    client: createFormantAuthClient(apiBase),
    coherenceLoginUrl,
    env: browserCoherenceEnv(),
  });
}

/**
 * Wires the selected auth strategy into the component tree and exposes the
 * current session as `useAuth()`. Same context shape the app has always
 * consumed — only the mechanism behind it changed.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<IAuthSession | null>(null);
  const [strategy] = useState<IAuthStrategy>(buildStrategy);

  useEffect(() => {
    let cancelled = false;
    let teardown: void | (() => void);
    fork(
      (async () => {
        try {
          teardown = await strategy.init((s) => {
            if (!cancelled) setSession(s);
          });
        } catch (err) {
          console.error('AuthProvider bootstrap failed', err);
        } finally {
          if (!cancelled) setReady(true);
        }
      })(),
    );
    return () => {
      cancelled = true;
      if (typeof teardown === 'function') teardown();
    };
  }, [strategy]);

  const value = useMemo<IAuthState>(
    () => ({
      ready,
      isAuthenticated: Boolean(session?.token),
      token: session?.token ?? null,
      email: session?.email ?? null,
      name: session?.name ?? null,
      orgName: session?.orgName ?? null,
      signIn: () => strategy.signIn(),
      signOut: () => strategy.signOut(),
      getConnectTokens: () => strategy.getConnectTokens(),
    }),
    [ready, session, strategy],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * The session when one is mounted, else null. For display helpers that must
 * render the same with or without a provider (unit tests, isolated previews).
 */
export function useOptionalAuth(): IAuthState | null {
  return useContext(AuthContext);
}

export function useAuth(): IAuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
